import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Grid, Skeleton, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { DataGrid } from '@mui/x-data-grid';
import { trTR } from '@mui/x-data-grid/locales';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'src/utils/axios';
import ExcelFileCreator from 'src/components/ExcelFileCreator';

import SectionCard from 'src/components/BpoMutabakat/SectionCard';
import InfoNote from 'src/components/BpoMutabakat/InfoNote';
import LevelBadge from 'src/components/BpoMutabakat/LevelBadge';
import { getSeriesColors } from 'src/components/BpoMutabakat/colors';
import {
  formatDuration, formatNumber, getBpoVendor
} from 'src/components/BpoMutabakat/format';
import { reportName } from 'src/components/BpoMutabakat/reports';

/**
 * RT 3 / 4 / 5 aynı tabloyu paylaşır; aralarındaki tek fark gruplamanın neye
 * göre yapıldığıdır. Bu yüzden üç ayrı kart değil, tek tablo ve üstünde geçiş
 * düğmesi var — kullanıcı aynı ölçüyü farklı kırılımda okuyor.
 */
const GRUPLAMALAR = [
  { reportType: 3, label: 'Kuyruk bazında', nameHeader: 'Kuyruk' },
  { reportType: 4, label: 'Departman bazında', nameHeader: 'Departman' },
  { reportType: 5, label: 'Özel gruplama', nameHeader: 'Özel grup' }
];

const QueueSkill = ({ date }) => {
  const theme = useTheme();
  const colors = getSeriesColors(theme);

  const [reportType, setReportType] = useState(3);
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const grouping = GRUPLAMALAR.find((g) => g.reportType === reportType);

  useEffect(() => {
    if (!date) return undefined;

    // Departman grafiği her zaman RT 4'ten beslenir; seçili kırılım da o ise
    // aynı ucu iki kez çağırmamak için tekilleştiriyoruz.
    const types = reportType === 4 ? [4] : [reportType, 4];
    let cancelled = false;

    setLoading(true);
    setError('');

    Promise.allSettled(types.map((rt) => axios.get(`/GetBpoMutabakatSkill/${date}/${rt}`)))
      .then((results) => {
        if (cancelled) return;

        const byType = {};
        results.forEach((result, i) => {
          byType[types[i]] =
            result.status === 'fulfilled' && Array.isArray(result.value?.data)
              ? result.value.data
              : [];
        });

        setRows(byType[reportType] || []);
        setDepartments(byType[4] || []);

        if (results.every((r) => r.status === 'rejected')) {
          const notFound = results.every((r) => r.reason?.response?.status === 404);
          setError(
            notFound
              ? 'Kuyruk & Skill servisi henüz yayında değil.'
              : 'Veriler alınamadı. Lütfen tekrar deneyin.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date, reportType]);

  /* ------------------------------------------- departman bazında hacim (RT 4) */
  const departmentChart = useMemo(() => {
    const items = [...departments].sort((a, b) => b.incoming - a.incoming);
    return {
      series: [{ name: 'Gelen çağrı', data: items.map((d) => d.incoming) }],
      options: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          animations: { enabled: false },
          fontFamily: theme.typography.fontFamily,
          background: 'transparent'
        },
        theme: { mode: theme.palette.mode },
        colors: [colors[0]],
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '62%' } },
        dataLabels: {
          enabled: true,
          formatter: (v) => formatNumber(v),
          style: { fontSize: '11px', fontWeight: 400 },
          offsetX: 26
        },
        grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
        xaxis: {
          categories: items.map((d) => d.name),
          labels: { formatter: (v) => formatNumber(Math.round(v)) }
        },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        tooltip: { theme: theme.palette.mode, y: { formatter: (v) => formatNumber(v) } }
      }
    };
  }, [departments, theme, colors]);

  /* ------------------------------------------------------------ tablo kolonlar */
  const columns = [
    { field: 'name', headerName: grouping.nameHeader, flex: 1.6, minWidth: 210 },
    { field: 'department', headerName: 'Departman', flex: 1, minWidth: 150 },
    {
      field: 'vendor',
      headerName: 'BPO',
      width: 100,
      valueGetter: (value, row) => getBpoVendor(row.name),
      renderCell: (params) => (
        <Box
          component="span"
          sx={(t) => ({
            px: 0.8,
            py: '2px',
            borderRadius: 0.75,
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
            color: 'text.secondary',
            backgroundColor:
              t.palette.mode === 'dark' ? 'rgba(255,255,255,.07)' : t.palette.grey[100]
          })}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'incoming',
      headerName: 'Gelen',
      type: 'number',
      width: 92,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'answered',
      headerName: 'Cevaplanan',
      type: 'number',
      width: 112,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'abandoned',
      headerName: 'Kaçan',
      type: 'number',
      width: 86,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'sl8515',
      headerName: 'SL %85/15',
      type: 'number',
      width: 124,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <LevelBadge value={params.value} />
    },
    {
      field: 'sl8020',
      headerName: 'SL %80/20',
      type: 'number',
      width: 124,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <LevelBadge value={params.value} />
    },
    {
      field: 'sl7030',
      headerName: 'SL %70/30',
      type: 'number',
      width: 124,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <LevelBadge value={params.value} />
    },
    {
      field: 'abandonRate',
      headerName: 'Kaçan oranı',
      type: 'number',
      width: 116,
      valueFormatter: (v) => (v === null || v === undefined ? '—' : `%${v}`)
    },
    {
      field: 'aht',
      headerName: 'AHT',
      type: 'number',
      width: 90,
      valueFormatter: (v) => formatDuration(v)
    },
    {
      field: 'maxWait',
      headerName: 'Maks. bekleme',
      type: 'number',
      width: 134,
      valueFormatter: (v) => formatDuration(v)
    },
    { field: 'maxAgent', headerName: 'Maks. agent', type: 'number', width: 118 }
  ];

  /* Departman kırılımında "Departman" zaten ana kolon; BPO firması yalnızca
     kuyruk adlarının son ekinden okunabiliyor. */
  const columnVisibility = {
    department: reportType !== 4,
    vendor: reportType === 3
  };

  const exportToExcel = () => {
    ExcelFileCreator(
      rows.map((r) => ({
        [grouping.nameHeader]: r.name,
        Departman: r.department,
        Gelen: r.incoming,
        Cevaplanan: r.answered,
        Kaçan: r.abandoned,
        'SL 85/15 %': r.sl8515,
        'SL 80/20 %': r.sl8020,
        'SL 70/30 %': r.sl7030,
        'Kaçan oranı %': r.abandonRate,
        'AHT sn': r.aht,
        'Maks. bekleme sn': r.maxWait,
        'Maks. agent': r.maxAgent
      })),
      `BPO Mutabakat - ${grouping.label} ${date}`
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, xl: 8 }}>
        <SectionCard
          title="Servis seviyesi"
          subtitle={`${reportName(reportType)} · ${formatNumber(rows.length)} satır`}
          action={
            <ToggleButtonGroup
              size="small"
              exclusive
              value={reportType}
              onChange={(e, value) => {
                if (value !== null) setReportType(value);
              }}
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.4 },
                // Tema primary.contrastText'i primary.main ile aynı turuncuya
                // ayarladığı için seçili düğmenin yazısını açıkça veriyoruz.
                '& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'primary.dark' }
                }
              }}
            >
              {GRUPLAMALAR.map((g) => (
                <ToggleButton key={g.reportType} value={g.reportType}>
                  {g.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
          padded={false}
        >
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              columnVisibilityModel={columnVisibility}
              loading={loading}
              density="compact"
              disableRowSelectionOnClick
              localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{ border: 0 }}
            />
          </Box>
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, xl: 4 }}>
        <SectionCard
          title="Departman bazında hacim"
          subtitle={reportName(4)}
          action={
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={loading || !rows.length}
            >
              Excel
            </Button>
          }
        >
          {loading ? (
            <Skeleton variant="rounded" height={360} />
          ) : (
            <ReactApexChart
              options={departmentChart.options}
              series={departmentChart.series}
              type="bar"
              height={Math.max(240, departments.length * 34 + 60)}
            />
          )}
        </SectionCard>
      </Grid>

      <Grid size={12}>
        <InfoNote>
          {error ? (
            <b>{error}</b>
          ) : (
            <>
              <b>Üç kırılım aynı tablodan okunur.</b> {reportName(3)}, {reportName(4)} ve{' '}
              {reportName(5)} tek tabloda tutulur; ayrım <code>REPORT_TYPE</code> kolonundadır.
              Üçü de servisten <code>-- Toplam</code> satırı döndürür, bu satırlar yüklemede
              atlanır &mdash; dolayısıyla buradaki toplamlar satırların kendi toplamıdır, çift
              sayım yoktur.
            </>
          )}
        </InfoNote>
      </Grid>
    </Grid>
  );
};

QueueSkill.propTypes = {
  date: PropTypes.string
};

export default QueueSkill;
